import { computed } from 'vue';
import { usePointerEvents } from '@/modules/map-editor';
import type { CanvasContexts, PointerEventContext, SelectionBox } from '@/modules/map-editor';

/**
 * Main pointer composable — orchestrator.
 * Coordinates all pointer-related logic.
 * This is the only composable that should be used from components.
 *
 * All store-related callbacks are managed internally by usePointerStateManagement,
 * so no external configuration is needed.
 */
export function usePointer() {
  const pointerEvents = usePointerEvents();

  /**
   * Build a PointerEventContext from component-level arguments.
   *
   * @param event   - Raw DOM event
   * @param canvas  - The canvas element
   * @param contexts - Canvas rendering contexts
   * @param zoomLevel - Current zoom level
   * @returns Context object expected by pointer event handlers
   */
  function buildContext(
    event: MouseEvent | TouchEvent,
    canvas: HTMLCanvasElement,
    contexts: CanvasContexts,
    zoomLevel: number
  ): PointerEventContext {
    return {
      event,
      canvas,
      contexts,
      zoomLevel,
      pixelRatio: window.devicePixelRatio
    };
  }

  /**
   * Handle pointer move (mouse or touch).
   */
  function pointerMove(
    event: MouseEvent | TouchEvent,
    contexts: CanvasContexts,
    canvas: HTMLCanvasElement,
    zoomLevel: number
  ): void {
    pointerEvents.handleMove(event, buildContext(event, canvas, contexts, zoomLevel));
  }

  /**
   * Handle pointer down (mouse or touch).
   */
  function pointerDown(
    event: MouseEvent | TouchEvent,
    contexts: CanvasContexts,
    canvas: HTMLCanvasElement,
    zoomLevel: number
  ): void {
    pointerEvents.handleDown(event, buildContext(event, canvas, contexts, zoomLevel));
  }

  /**
   * Handle pointer up (mouse or touch).
   */
  function pointerUp(
    event: MouseEvent | TouchEvent,
    contexts: CanvasContexts,
    canvas: HTMLCanvasElement,
    zoomLevel: number
  ): void {
    pointerEvents.handleUp(event, buildContext(event, canvas, contexts, zoomLevel));
  }

  /**
   * Handle double click.
   */
  function pointerDoubleClick(
    event: MouseEvent,
    contexts: CanvasContexts,
    zoomLevel: number
  ): void {
    pointerEvents.handleDoubleClick(
      event,
      buildContext(event, event.currentTarget as HTMLCanvasElement, contexts, zoomLevel)
    );
  }

  /**
   * Handle context menu (right-click).
   */
  function pointerContextMenu(event: MouseEvent): void {
    pointerEvents.handleContextMenu(event);
  }

  /** Reactive selection box from pointer state — null when not dragging a lasso */
  const selectionBox = computed<SelectionBox | null>(() => pointerEvents.pointerState.value.selectionBox);

  /** Whether the cursor is hovering over the multi-selection bounding box */
  const isHoveringSelectionBox = computed<boolean>(() => pointerEvents.pointerState.value.isHoveringSelectionBox);

  return {
    computedPointerClassname: pointerEvents.computedCursor,
    selectionBox,
    isHoveringSelectionBox,
    pointerMove,
    pointerDown,
    pointerUp,
    pointerDoubleClick,
    pointerContextMenu,
    handleKeyDown: pointerEvents.handleKeyDown,
    handleKeyUp: pointerEvents.handleKeyUp
  };
}
